/**
 * ════════════════════════════════════════════════════════════════════
 * Intelligent Routing API Client — v3.2.20
 * ════════════════════════════════════════════════════════════════════
 * Frontend wrapper for /api/intelligent/*
 *
 * Every portal that needs AI should call this INSTEAD of /ai/* endpoints.
 * This guarantees every AI response goes through:
 *   - Intent detection
 *   - Specialty validation
 *   - Multi-agent fan-out (when needed)
 *   - Hallucination guard verification
 *
 * Usage:
 *   import { intelligentApi } from '../services/intelligentApi';
 *   const result = await intelligentApi.route({
 *     text: 'My cocoa trees have swollen shoots and the leaves are yellowing',
 *     context: { feature: 'pest_scan', portal: 'FarmerDashboard' }
 *   });
 *   console.log(result.response);          // the natural-language response
 *   console.log(result.routing);            // routing metadata
 *   console.log(result.verification);       // hallucination check report
 */
import { apiClient } from './api';

export interface RoutingContext {
  feature?: string;
  portal?: string;
  farmId?: string;
  batchId?: string;
  language?: string;
  photoUrl?: string;
  polygonGeoJson?: any;
  [key: string]: any;
}

export interface RouteOptions {
  forceSingleAgent?: string;
  forceMultiAgent?: boolean;
  skipVerification?: boolean;
  maxAgents?: number;
}

export interface RouteRequest {
  text: string;
  context?: RoutingContext;
  options?: RouteOptions;
}

export interface RoutingMetadata {
  mode: 'single_agent' | 'multi_agent_fanout' | 'copilot_fallback';
  selected_agents: string[];
  detected_candidates: Array<{ code: string; matchedKeywords: string[]; priority: number }>;
  specialty_violations: Array<{ agentCode: string; reason: string }>;
  duration_ms: number;
  decision_id: string;
}

export interface VerificationReport {
  passed: boolean;
  confidence: number;
  confidence_floor: number;
  sources_cited: string[];
  checks: Array<{ type: string; passed: boolean; detail: string }>;
}

export interface RouteResponse {
  response: any;
  full: any;
  routing: RoutingMetadata;
  verification: VerificationReport | null;
}

export interface RoutingStats {
  totalDecisions: number;
  multiAgentDecisions: number;
  copilotFallbacks: number;
  specialtyViolations: number;
  hallucinationBlocks: number;
  avgDurationMs: number;
}

export interface RoutingDecisionRow {
  id: string;
  request_text: string;
  triggered_by: string;
  routing_mode: string;
  selected_agents: string[];
  specialty_violations: any[];
  started_at: string;
  completed_at: string;
  duration_ms: number;
  error: string | null;
}

export interface AgentSpecialtyRow {
  agent_code: string;
  agent_name: string;
  specialty: string;
  features: string[];
  keywords: string[];
  task_types: string[];
  multi_agent_eligible: boolean;
  priority: number;
  description: string;
  is_active: boolean;
}

export const intelligentApi = {
  /**
   * Main entry point: route a request intelligently.
   * Every portal should call this instead of /ai/* endpoints.
   */
  route: async (req: RouteRequest): Promise<RouteResponse> => {
    const res = await apiClient.post('/intelligent/route', req);
    return res.data;
  },

  /**
   * Get recent routing decisions (admin only).
   */
  getDecisions: async (limit = 50): Promise<{ decisions: RoutingDecisionRow[]; count: number }> => {
    const res = await apiClient.get('/intelligent/decisions', { params: { limit } });
    return res.data;
  },

  /**
   * Get a single routing decision with its hallucination audits (admin only).
   */
  getDecisionDetail: async (id: string): Promise<{ decision: RoutingDecisionRow; audits: any[] }> => {
    const res = await apiClient.get(`/intelligent/decisions/${id}`);
    return res.data;
  },

  /**
   * Get aggregate routing stats (last 24h, admin only).
   */
  getStats: async (): Promise<RoutingStats> => {
    const res = await apiClient.get('/intelligent/stats');
    return res.data;
  },

  /**
   * List all agent specialty bindings (admin only).
   */
  getAgents: async (): Promise<{ agents: AgentSpecialtyRow[]; count: number; source?: string }> => {
    const res = await apiClient.get('/intelligent/agents');
    return res.data;
  },

  /**
   * Get hallucination audit for a specific decision (admin only).
   */
  getAudit: async (decisionId: string): Promise<{ audits: any[]; count: number }> => {
    const res = await apiClient.get(`/intelligent/audit/${decisionId}`);
    return res.data;
  },

  /**
   * Quick health check (admin only).
   */
  getHealth: async (): Promise<{
    status: string;
    routing_decisions_24h: number;
    hallucination_blocks_24h: number;
    specialty_violations_24h: number;
    multi_agent_decisions_24h: number;
    copilot_fallbacks_24h: number;
    avg_duration_ms: number;
    timestamp: string;
  }> => {
    const res = await apiClient.get('/intelligent/health');
    return res.data;
  },
};
